import type {
  AppBuildDto,
  AppDistributionDto,
  EndpointCheckResult,
  HealthResponse,
  UpdateAppDistributionRequest,
} from '@field-support/shared';
import { api, http, upload } from '@/shared/api';

/** The server's own addresses, as the agents' phones would have to reach it. */
export interface ServerEndpoints {
  publicIp: string | null;
  localIp: string | null;
  currentPublicApiUrl: string;
  suggestedApiUrl: string;
}

/**
 * `reasonCode` is what the interface translates; `error` is the server's English
 * diagnostic (technical detail, shown verbatim as such).
 */
export type EndpointCheck = EndpointCheckResult;

export type BuildAction = 'publish' | 'rotate-token' | 'revoke-token';

export function fetchDistribution(signal?: AbortSignal): Promise<AppDistributionDto> {
  return api<AppDistributionDto>('/engine/app-distribution', { signal });
}

export function fetchServerEndpoints(signal?: AbortSignal): Promise<ServerEndpoints> {
  return api<ServerEndpoints>('/engine/app-distribution/endpoints', { signal });
}

/** The server fetches the address itself (private and metadata ranges refused) and reports. */
export function checkEndpoint(url: string): Promise<EndpointCheck> {
  return api<EndpointCheck>('/engine/app-distribution/check-endpoint', { method: 'POST', body: { url } });
}

/** Minimum version and the two external install links (Android, iPhone/TestFlight); null clears. */
export function updateDistribution(body: UpdateAppDistributionRequest): Promise<AppDistributionDto> {
  return api<AppDistributionDto>('/engine/app-distribution', { method: 'PUT', body });
}

/**
 * Uploads an APK (multipart, with progress). `version` and `releaseNotes` go
 * before the file: the server reads the stream in order. It inspects the APK
 * and publishes it.
 */
export function uploadBuild(
  file: File,
  fields: { version: string; releaseNotes: string },
  onProgress?: (fraction: number) => void,
): Promise<AppBuildDto> {
  return upload<AppBuildDto>('/engine/app-distribution/builds', file, fields, { onProgress });
}

/** Publish a stored build, or rotate / revoke its public link. Answers with the whole overview. */
export function runBuildAction(id: string, action: BuildAction): Promise<AppDistributionDto> {
  return api<AppDistributionDto>(`/engine/app-distribution/builds/${id}/${action}`, { method: 'POST' });
}

/** Removes the build and its file; deleting the published one leaves nothing published. */
export function deleteBuild(id: string): Promise<void> {
  return api<void>(`/engine/app-distribution/builds/${id}`, { method: 'DELETE' });
}

/**
 * The public health check. A degraded server answers 503 with the same body,
 * which is an answer here, not a failure.
 */
export async function fetchHealth(signal?: AbortSignal): Promise<HealthResponse> {
  const response = await http.get<HealthResponse>('/health', { signal, validateStatus: (status) => status === 200 || status === 503 });
  return response.data;
}
